/**
 * `useOffsetPage` — reads one page of any oRPC list procedure that pages with
 * `paginationParams` (`offset` + `limit`), for a numbered table. The previous
 * page stays on screen while the next one loads.
 *
 * It injects the silo and the reading locale the way `useSiloQuery` does.
 */
import type { ProcedureUtils } from '@orpc/react-query';
import type { LocaleSelector } from '@repo/shared/orpc/translations';
import { keepPreviousData } from '@tanstack/react-query';

import { useSiloQuery } from './use-silo-query';

type OffsetPage = { items: unknown[] };
type OffsetInput = { siloKey: string; offset?: number; limit?: number; locale?: LocaleSelector };
type OffsetProcedure<TInput, TPage extends OffsetPage> = ProcedureUtils<any, TInput, TPage, any>;

/**
 * One page of an offset-paginated list. `offset` and `limit` are required, so a
 * table always says which page it shows.
 */
export function useOffsetPage<TInput extends OffsetInput, TPage extends OffsetPage>({
    procedure,
    input,
    enabled = true,
}: {
    procedure: OffsetProcedure<TInput, TPage>;
    input: Omit<TInput, 'siloKey' | 'locale' | 'offset' | 'limit'> & { offset: number; limit: number };
    enabled?: boolean;
}) {
    // `Omit` hides that a required offset and limit fit TInput's optional ones.
    const pageInput = input as Omit<TInput, 'siloKey' | 'locale'>;
    return useSiloQuery({ procedure, input: pageInput, enabled, options: { placeholderData: keepPreviousData } });
}
